import {debounce, onBlockLoaded} from "@plaudit/frontend-apis";

declare const fanBladeLookupData: {columns: string[], rows: string[][]};

type Step = {csvColumn: string, csvColumnIndex: number, inputWrapper: HTMLElement, dropdown: HTMLSelectElement, label: string, element: HTMLElement};
type FanBladeLookupDataTreeNode = {[k in string]: FanBladeLookupDataTreeNode|string};

declare global {
	interface Window {
		fanBladeLookupDataTree: FanBladeLookupDataTreeNode;
	}
}

class FanBladeLookupManager {
	private readonly stepsWrapper: HTMLElement;
	private readonly steps: Step[];
	private readonly resultStep: Omit<Step, 'dropdown'|'label'|'inputWrapper'>;
	private readonly fieldBase: HTMLElement;

	/**
	 * This cache holds the *incoming* rows for each level
	 * @private
	 */
	private readonly filteredOptionsCache: string[][][] = [fanBladeLookupData.rows];

	constructor(
		private readonly blockRoot: HTMLElement
	) {
		const dropdownsWrapper = blockRoot.querySelector("form");
		const stepsWrapper = blockRoot.querySelector<HTMLElement>(".steps");

		if (!dropdownsWrapper || !stepsWrapper) {
			throw "Missing crucial root elements. Stopping.";
		}
		this.stepsWrapper = stepsWrapper;
		this.fieldBase = dropdownsWrapper.firstElementChild as HTMLElement;
		this.fieldBase.remove();

		this.resultStep = this.buildResultStep();

		// Extract dropdown configurations from step elements
		const stepElements = Array.from(stepsWrapper.querySelectorAll<HTMLElement>(".wp-block-plaudit-site-fan-blade-lookup-step"));
		this.steps = stepElements.map((element, index) => {
			const csvColumn = element.dataset.spreadsheetColumn ?? "";
			const csvColumnIndex = fanBladeLookupData.columns.indexOf(csvColumn);
			// Validate CSV column references
			if (csvColumnIndex < 0) {
				throw `CSV column "${csvColumn}" not found in fanBladeLookupData.columns`;
			}
			const label = element.dataset.dropdownLabel ?? "";
			return {csvColumn, csvColumnIndex, ...this.createDropdown(label, index), label, element};
		});

		this.replaceTextPlaceholdersWithElements();

		// Build dropdown select elements
		dropdownsWrapper.append(...this.steps.map(config => config.inputWrapper));
		this.updateFollowingDropdownOptions(-1); // This initializes the first dropdown
		this.showLatestStep();
	}

	private buildResultStep(): FanBladeLookupManager['resultStep'] {
		// Move the result element to the end of stepsWrapper
		const resultElement = this.stepsWrapper.querySelector<HTMLElement>(".wp-block-plaudit-site-fan-blade-lookup-result");
		if (!resultElement) {
			throw "Missing the result element. Stopping.";
		}

		const csvColumn = resultElement.dataset.spreadsheetColumn ?? "";
		const csvColumnIndex = fanBladeLookupData.columns.indexOf(csvColumn);
		// Validate CSV column references
		if (csvColumnIndex < 0) {
			throw `CSV column "${csvColumn}" not found in fanBladeLookupData.columns`;
		}

		// Move the result element to the end of stepsWrapper
		this.stepsWrapper.appendChild(resultElement);

		return {csvColumn, csvColumnIndex, element: resultElement};
	}

	private createDropdown(label: string, index: number): {dropdown: HTMLSelectElement, inputWrapper: HTMLElement} {
		const inputWrapper = this.fieldBase.cloneNode(true) as HTMLElement;
		inputWrapper.querySelector<HTMLElement>(".label-text")!.innerText = label;
		const dropdown = inputWrapper.querySelector("select")!;

		dropdown.addEventListener("change", () => {
			this.updateFollowingDropdownOptions(index);
			this.showLatestStep();
		});

		return {dropdown, inputWrapper};
	}

	private updateFollowingDropdownOptions(index: number) {
		this.filteredOptionsCache.splice(index + 1, this.filteredOptionsCache.length);
		this.populateFilteredOptionsCache(index); // Ensure that the preceding cache lines have been populated

		let i = index;
		if (this.steps[index]?.dropdown.value !== "__NO_SELECTION__") {
			for (i++; i < this.steps.length; i++) {
				const dropdown = this.steps[i].dropdown;
				dropdown.disabled = false;
				const availableOptions = this.getOptionsForDropdown(i);
				const existingValue = dropdown.value;
				const canRetainValue = availableOptions.includes(existingValue);
				dropdown.replaceChildren(dropdown.item(0)!, ...availableOptions.map(option => {
					const optionElement = document.createElement("option");
					optionElement.value = option;
					optionElement.append(option);
					if (canRetainValue && option === existingValue) {
						optionElement.selected = true;
					}
					return optionElement;
				}));
				if (!canRetainValue) {
					dropdown.value = "__NO_SELECTION__";
				}
				if (dropdown.value === "__NO_SELECTION__") {
					break;
				}
			}
		}

		for (i++; i < this.steps.length; i++) {
			const dropdown = this.steps[i].dropdown;
			dropdown.disabled = true;
			dropdown.value = "__NO_SELECTION__";
		}
	}

	private showLatestStep() {
		for (const managedStep of this.stepsWrapper.querySelectorAll<HTMLElement>(".managed-step")) {
			managedStep.style.display = "none";
		}
		const firstWaitingElement = this.steps.find(({dropdown}) => dropdown.value === "__NO_SELECTION__")?.element;
		if (firstWaitingElement) {
			firstWaitingElement.style.display = "";
			return;
		}

		this.updateFilteredOptionsCacheLine(this.steps.length);
		const result = this.getOptionsForDropdown(this.steps.length)[0];

		//Then it's time to show results
		for (const marker of this.blockRoot.querySelectorAll(".plaudit-site-fan-blade-lookup-result-marker")) {
			marker.textContent = result;
		}
		for (const anchor of this.blockRoot.querySelectorAll("a")) {
			if (decodeURI(anchor.href).includes("{selected_item}")) {
				anchor.dataset.hrefBackup = decodeURI(anchor.href);
			}
			if (anchor.dataset.hrefBackup?.includes("{selected_item}")) {
				anchor.href = anchor.dataset.hrefBackup?.replaceAll("{selected_item}", result);
			}
		}
		this.resultStep.element.style.display = "";
	}

	private getOptionsForDropdown(index: number) {
		if (this.filteredOptionsCache.length <= index || !this.filteredOptionsCache[index]) {
			this.updateFilteredOptionsCacheLine(index);
			if (!this.filteredOptionsCache[index]) {
				throw `Unable to resolve options for ${this.steps[index].label}`;
			}
		}
		const columnIndex = index < this.steps.length ? this.steps[index].csvColumnIndex : this.resultStep.csvColumnIndex;
		const options = Array.from(new Set(this.filteredOptionsCache[index].map(row => row[columnIndex])));
		const otherIndex = options.indexOf("Other");
		if (otherIndex !== -1) {
			options.splice(otherIndex, 1);
		}
		const intRegex = /^\d+$/;
		let sortedOptions: string[];
		if (options.every(option => intRegex.test(option))) {
			sortedOptions = options.map<[number, string]>(option => [parseInt(option), option])
				.sort((a, b) => a[0] - b[0]).map(option => option[1]);
		} else {
			const floatRegex = /^(?:\d+(?:\.\d*)?|\.\d+)$/;
			if (options.every(option => floatRegex.test(option))) {
				sortedOptions = options.map<[number, string]>(option => [parseFloat(option), option])
					.sort((a, b) => a[0] - b[0]).map(option => option[1]);
			} else {
				sortedOptions = options.sort();
			}
		}

		if (otherIndex !== -1) {
			sortedOptions.push("Other")
		}
		return sortedOptions;
	}
	private populateFilteredOptionsCache(limit: number) {
		if (this.filteredOptionsCache.length <= 0) {
			this.filteredOptionsCache.push(fanBladeLookupData.rows);
		}
		for (let i = 1; i <= limit; i++) {
			this.updateFilteredOptionsCacheLine(i);
		}
	}

	private updateFilteredOptionsCacheLine(index: number) {
		if (this.filteredOptionsCache[index]) {
			return;
		}
		const value = this.steps[index - 1].dropdown.value;
		if (value === "__NO_SELECTION__") {
			return;
		}
		const columnIndex = this.steps[index - 1].csvColumnIndex;
		const updatedValues = this.filteredOptionsCache[index - 1].filter(row => row[columnIndex] === value);
		if (index < this.filteredOptionsCache.length) {
			this.filteredOptionsCache[index] = updatedValues;
		} else if (index === this.filteredOptionsCache.length) {
			this.filteredOptionsCache.push(updatedValues);
		} else {
			throw "Attempted to populate the filtered options cache out of order";
		}
	}

	private replaceTextPlaceholdersWithElements() {
		const nodeIterator = document.createNodeIterator(this.blockRoot, NodeFilter.SHOW_TEXT);
		for (let node: ReturnType<typeof nodeIterator.nextNode>; (node = nodeIterator.nextNode()) !== null;) {
			if (!(node instanceof Text)) {
				continue;
			}
			const placeholderPosition = node.data.indexOf("{selected_item}");
			if (placeholderPosition < 0) {
				continue;
			}
			const placeholderHolder = node.splitText(placeholderPosition);
			placeholderHolder.splitText(15 /* length of "{selected_item}" */);
			const resultLink = document.createElement("span");
			resultLink.classList.add("plaudit-site-fan-blade-lookup-result-marker");
			placeholderHolder.replaceWith(resultLink);
		}
	}
}

onBlockLoaded({
	blockOrClassName: "plaudit-site/fan-blade-lookup",
	onAdd(blockRoot) {
		if (window.self !== window.top) {
			for (const link of blockRoot.querySelectorAll("a")) {
				link.target = "_top";
			}
			blockRoot.style.width = "100%";
		}

		// blockRoot is the root element of the block (aka the element that has the wp-block-plaudit-fan-blade-lookup class on it)
		// You can add any logic that needs to run once per block here
		new FanBladeLookupManager(blockRoot);
	}
	// If you need to add logic that runs once per removal, add onRemove(blockRoot) {} to this object
});

if (window.self !== window.top) {
	let targetOrigin = "https://shop.hortonww.com";
	const sendDimensionsUpdate = debounce(() => {
		window.parent.postMessage({type: 'horton:fan-finder:dimensions', width: document.body.scrollWidth, height: document.body.scrollHeight}, targetOrigin);
	}, 100);
	new ResizeObserver(sendDimensionsUpdate).observe(document.body);
	sendDimensionsUpdate();
	window.addEventListener('message', ({data}) => {
		if (typeof data !== 'object' || !data) {
			return;
		}
		if (data.type === 'horton:fan-finder:dimensions-request') {
			sendDimensionsUpdate();
			return;
		}
		if (data.type === 'horton:fan-finder:set-wrapper-origin') {
			targetOrigin = data.origin;
			sendDimensionsUpdate();
			return;
		}
	});

}
